import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useDocumentStore = create((set) => ({
  documents: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get documents
  getDocuments: async ({ query = null, successCallback }) => {
    // set({ documentLoading: true });
    useLoadingStore.getState().setLoading(true);

    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_DOCUMENTS,
        null,
        query
      );

      if (response.success) {
        set((state) => ({
          documents: response.data?.data || [],
          pagination: {
            ...state.pagination,
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        }));

        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      // set({ documentLoading: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create the document
  createDocument: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_DOCUMENT,
        data
      );

      if (response.success) {
        await useDocumentStore.getState().getDocuments({
          query: {
            page: 1,
            limit: 10,
          },
          successCallback: () => {
            useDocumentStore.getState().resetCurrentPage();
            successCallback && successCallback(response);
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete document
  deleteDocument: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_DOCUMENT,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedCategories = state.documents.filter(
            (document) => document.id !== id
          );

          const updatedTotalItems = updatedCategories.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            documents: updatedCategories,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update document
  updateDocument: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_DOCUMENT,
        data,
        null,
        id
      );

      if (response.success) {
        await useDocumentStore.getState().getDocuments({
          successCallback: () => {
            useDocumentStore.getState().resetCurrentPage(); // Reset page
            successCallback && successCallback(response);
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to active/inactive document
  toggleDocumentStatus: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.ACTIVE_INACTIVE_DOCUMENT,
        data,
        null,
        id
      );
      if (response.success) {
        set((state) => ({
          documents: state.documents.map((document) =>
            document.id === id ? { ...document, ...data } : document
          ),
        }));
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useDocumentStore;
